import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";
import {
  Box,
  Button,
  Paper,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";

import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import UndoIcon from "@mui/icons-material/Undo";

import MinervaEngine from "minerva-engine"; // Import MinervaEngine

import { pieceSets } from "../style/pieceset";
import { themeColors } from "../style/boardtheme";
import { ColorModeContext } from "../context/ColorModeContext";
import "./AnalysisBoard.css";

function AnalysisBoard() {
  const { mode } = useContext(ColorModeContext); // Get the current mode from context

  const [game, setGame] = useState(new Chess());
  const [pgn, setPgn] = useState("");
  const [historicEvaluations, setHistoricEvaluations] = useState([]);
  const [historicMoveEvaluations, setHistoricMoveEvaluations] = useState([]);
  const [historicTargetSquares, setHistoricTargetSquares] = useState([]);
  const [historicNarration, setHistoricNarration] = useState([]);
  const [processedNarration, setProcessedNarration] = useState([]);
  const [orientation, setOrientation] = useState("white");
  const [renderedImage, setRenderedImage] = useState(null);
  const [minervaEngine, setMinervaEngine] = useState(null);
  const [selectedNarrationType, setSelectedNarrationType] = useState("");

  const [showMoves, setShowMoves] = useState(false);
  const toggleShowMoves = (event) => {
    setShowMoves(event.target.checked); // Set based on the switch's checked state
  };

  const stockfishWorker = useRef(null);
  const chessboardRef = useRef(null);

  const influenceColors = {
    Attacking: "rgba(255, 0, 0, 0.2)",
    Defending: "rgba(0, 255, 0, 0.2)",
    Controlling: "rgba(0, 0, 255, 0.10)",
    Outpost: "rgba(0, 0, 255, 0.10)",
    Check: "rgba(255, 0, 0, 0.2)",
    Checkmate: "rgba(255, 0, 0, 0.2)",
  };

  const getSquareStyles = (historicNarration) => {
    // Access the latest narration entry
    const latestNarration = historicNarration[historicNarration.length - 1];
    if (!Array.isArray(latestNarration)) return {}; // Return empty if latest is not array

    const styles = {};
    latestNarration.forEach((narration) => {
      const { type, sub_type } = narration;
      const { square } = narration.details;

      // Skip the iteration if type matches selectedNarrationType
      if (type !== selectedNarrationType) {
        return; // Skips to the next iteration in forEach
      }

      if (influenceColors[sub_type]) {
        styles[square] = { backgroundColor: influenceColors[sub_type] };
      }
    });
    return styles;
  };

  const renderNarrationWithColorBoxes = (narrationText, type, sub_type) => {
    if (type !== selectedNarrationType) {
      // Display plain text, including punctuation
      return (
        <span
          style={{
            display: "inline-flex",
            flexWrap: "wrap",
            lineHeight: 1.5,
            whiteSpace: "normal",
          }}
        >
          {narrationText.split(" ").map((word, index) => (
            <span
              key={index}
              style={{
                display: "inline-block",
                padding: "0 1px",
                fontSize: "inherit",
                margin: "0 1px",
              }}
            >
              {word}
            </span>
          ))}
        </span>
      );
    }

    return (
      <span
        style={{
          flexWrap: "wrap",
          display: "inline-flex",
          lineHeight: 1.5,
          whiteSpace: "normal",
        }}
      >
        {narrationText.split(" ").map((word, index) => {
          // Strip punctuation from moves only when coloring is active
          const cleanWord = word.replace(/[.,]/g, "");
          const isSquare = /^[a-h][1-8]$/.test(cleanWord);

          return (
            <span
              key={index}
              style={{
                display: "inline-block",
                backgroundColor: isSquare
                  ? influenceColors[sub_type]
                  : "transparent",
                borderRadius: isSquare ? "5px" : "0",
                padding: isSquare ? "0 5px" : "0 1px",
                margin: "0 1px",
                fontSize: "inherit",
              }}
            >
              {isSquare ? cleanWord : word}{" "}
              {/* Use cleanWord only for squares */}
            </span>
          );
        })}
      </span>
    );
  };

  const getEvaluationWithMinerva = useCallback(
    async (fen) => {
      if (minervaEngine) {
        try {
          const evaluationResult = await minervaEngine.getEvaluation(fen);

          if (evaluationResult.length > 0) {
            setHistoricEvaluations((prev) => [...prev, evaluationResult]);
          }
        } catch (error) {
          console.error("Error evaluating with Minerva:", error);
        }
      }
    },
    [minervaEngine]
  );

  const handleResetBoard = useCallback(() => {
    const newGame = new Chess();
    setPgn("");
    setHistoricMoveEvaluations([]);
    setHistoricEvaluations([]);
    setHistoricTargetSquares([]);
    setHistoricNarration([]);
    setProcessedNarration([]);
    setGame(newGame);
    getEvaluationWithMinerva(newGame.fen());
  }, [getEvaluationWithMinerva]);

  async function onDrop(sourceSquare, targetSquare) {
    setRenderedImage(null);
    try {
      const move = game.move({ from: sourceSquare, to: targetSquare });
      if (move === null) return false;

      setHistoricTargetSquares((prev) => [...prev, targetSquare]);

      let history = game.history();
      let formattedHistory = "";
      for (let i = 0; i < history.length; i += 2) {
        const moveNumber = Math.floor(i / 2) + 1;
        const whiteMove = history[i];
        const blackMove = history[i + 1] ? history[i + 1] : "";
        formattedHistory += `${moveNumber}. ${whiteMove} ${blackMove} `;
      }
      setPgn(formattedHistory.trim());

      // Get Move Evaluation
      await minervaEngine
        .getMoveEvaluation(formattedHistory.trim())
        .then((result) => {
          setHistoricMoveEvaluations((prev) => [...prev, result]);
        })
        .catch(console.error);

      // Get the Narration
      await minervaEngine
        .getNarration(formattedHistory.trim())
        .then((result) => {
          setHistoricNarration((prev) => [...prev, result]); // Store the result in the history
          setProcessedNarration(processNarration(result));
        })
        .catch(console.error);

      // Get overall position evaluation
      getEvaluationWithMinerva(game.fen());

      return true;
    } catch (error) {
      return false;
    }
  }

  const flipOrientation = () => {
    setOrientation((prev) => (prev === "white" ? "black" : "white"));
  };

  const getSquarePosition = (square) => {
    if (chessboardRef.current) {
      const rect = chessboardRef.current.getBoundingClientRect(); // Get chessboard dimensions and position
      const squareSize = rect.width / 8; // Assuming it's a square chessboard

      // Get the rank (1-8) and file (a-h) from the square (e.g., 'a1', 'e4')
      const files = "abcdefgh";
      const ranks = "12345678";

      const fileIndex = files.indexOf(square[0]);
      const rankIndex = ranks.indexOf(square[1]);

      // Calculate the left and top position of the square
      const left = rect.left + fileIndex * squareSize;
      const top = rect.top + (7 - rankIndex) * squareSize; // Subtract rankIndex to handle from bottom up

      return { left, top, size: squareSize };
    }
    return null;
  };

  // Helper function to format square lists with commas and "and"
  const formatSquareList = useCallback((squares) => {
    if (squares.length <= 1) return squares.join(", ");
    return `${squares.slice(0, -1).join(", ")} and ${
      squares[squares.length - 1]
    }`;
  }, []);

  // Helper function to pluralize words based on count
  const pluralize = useCallback(
    (word, count) => (count > 1 ? `${word}s` : word),
    []
  );

  const processPieceInfluence = useCallback(
    (pieceInfluenceNarrations) => {
      // Accumulate controlling squares, attacking pieces, and defending pieces
      const result = pieceInfluenceNarrations.reduce(
        (acc, narration) => {
          const { sub_type, priority } = narration;
          const { piece, square } = narration.details;

          if (sub_type === "Controlling") {
            acc.controllingSquares.push({ square, priority });
          } else if (sub_type === "Attacking") {
            const key = `Attacking ${piece}`;
            acc.attacking[key] = acc.attacking[key] || [];
            acc.attacking[key].push({ square, priority });
          } else if (sub_type === "Defending") {
            const key = `Defending ${piece}`;
            acc.defending[key] = acc.defending[key] || [];
            acc.defending[key].push({ square, priority });
          }

          return acc;
        },
        { controllingSquares: [], attacking: {}, defending: {} }
      );

      // Format controlling squares narrations as JSON objects
      const controllingNarrations =
        result.controllingSquares.length > 0
          ? [
              {
                type: "PIECE_INFLUENCE",
                sub_type: "Controlling",
                priority: Math.min(
                  ...result.controllingSquares.map((item) => item.priority)
                ),
                description: `Controlling the ${formatSquareList(
                  result.controllingSquares.map((item) => item.square)
                )} ${pluralize("square", result.controllingSquares.length)}`,
              },
            ]
          : [];

      // Format attacking narrations as JSON objects
      const attackingNarrations = Object.entries(result.attacking).map(
        ([action, squares]) => {
          const [sub_type, piece] = action.split(" ");
          const pluralizedPiece = pluralize(piece, squares.length);

          return {
            type: "PIECE_INFLUENCE",
            sub_type: sub_type,
            priority: Math.min(...squares.map((item) => item.priority)),
            description: `Attacking the ${pluralizedPiece} on the ${formatSquareList(
              squares.map((item) => item.square)
            )} ${pluralize("square", squares.length)}`,
          };
        }
      );

      // Format defending narrations as JSON objects
      const defendingNarrations = Object.entries(result.defending).map(
        ([action, squares]) => {
          const [sub_type, piece] = action.split(" ");
          const pluralizedPiece = pluralize(piece, squares.length);

          return {
            type: "PIECE_INFLUENCE",
            sub_type: sub_type,
            priority: Math.min(...squares.map((item) => item.priority)),
            description: `Defending the ${pluralizedPiece} on the ${formatSquareList(
              squares.map((item) => item.square)
            )} ${pluralize("square", squares.length)}`,
          };
        }
      );

      // Combine all narrations into a single array of JSON objects
      return [
        ...controllingNarrations,
        ...attackingNarrations,
        ...defendingNarrations,
      ];
    },
    [formatSquareList, pluralize]
  ); // Dependency array for useCallback

  // Function to process OUTPOST_PIECE narrations
  const processOutpostPieces = (outpostPieceNarrations) => {
    if (
      !Array.isArray(outpostPieceNarrations) ||
      outpostPieceNarrations.length === 0
    )
      return [];

    // Map each outpost narration to a descriptive string
    const outpostTexts = outpostPieceNarrations.map((narration) => {
      const { piece, square } = narration.details;
      return {
        type: narration.type,
        sub_type: narration.sub_type,
        priority: narration.priority,
        description: `The ${piece} has secured an outpost on ${square}`,
      };
    });

    return outpostTexts; // Return the array of outpost descriptions
  };

  const processCheck = (checkNarrations) => {
    if (!Array.isArray(checkNarrations) || checkNarrations.length === 0)
      return [];

    // Map each outpost narration to a descriptive string
    const outpostTexts = checkNarrations.map((narration) => {
      const { square, attackingPiece } = narration.details;
      return {
        type: narration.type,
        sub_type: narration.sub_type,
        priority: narration.priority,
        description: `The ${attackingPiece} ${
          narration.sub_type === "Check" ? "Checked" : "Checkmated"
        } the King on ${square}`,
      };
    });

    return outpostTexts; // Return the array of outpost descriptions
  };

  function processOpeningNames(narration) {
    if (!narration[0]) {
      return null;
    }

    const { name, eco, transposition } = narration[0].details;

    const description = `The Opening is the ${name} (${eco})${
      transposition ? ", reached by transposition" : ""
    }`;

    return {
      type: narration[0].type,
      sub_type: narration[0].sub_type,
      priority: narration[0].priority,
      description: description,
    };
  }

  // Main processNarration function
  const processNarration = useCallback(
    (narrations) => {
      if (!Array.isArray(narrations) || narrations.length === 0) return [];

      // Filter OPENING_DETECTION narrations
      const openingNameNarrations = narrations.filter(
        (n) => n.type === "OPENING_DETECTION"
      );

      // Filter PIECE_INFLUENCE narrations
      const pieceInfluenceNarrations = narrations.filter(
        (n) => n.type === "PIECE_INFLUENCE"
      );

      // Filter OUTPOST_PIECE narrations
      const outpostPieceNarrations = narrations.filter(
        (n) => n.type === "OUTPOST_PIECE"
      );

      // Filter Check narrations
      const checkNarrations = narrations.filter((n) => n.type === "CHECK");

      // Process each type of narration separately
      const processedOpeningNames = processOpeningNames(openingNameNarrations);
      const processedPieceInfluence = processPieceInfluence(
        pieceInfluenceNarrations
      );
      const processedOutpostPieces = processOutpostPieces(
        outpostPieceNarrations
      );
      const processedCheck = processCheck(checkNarrations);

      // Combine the processed results
      const combinedNarrations = [
        processedOpeningNames,
        ...processedPieceInfluence,
        ...processedOutpostPieces,
        ...processedCheck,
      ];

      // Sort by priority in ascending order (highest priority = 1 at the top)
      const sortedNarration = combinedNarrations.sort(
        (a, b) => a.priority - b.priority
      );

      // Set selectedNarrationType to the minimum type in the sorted result
      if (sortedNarration.length > 0) {
        setSelectedNarrationType(sortedNarration[0].type);
      }

      return sortedNarration;
    },
    [setSelectedNarrationType, processPieceInfluence]
  );

  const handleUndo = useCallback(() => {
    if (game.history().length === 0) {
      return; // If no moves are available, return immediately
    }
    game.undo();

    setProcessedNarration(
      processNarration(historicNarration[historicNarration.length - 2])
    );

    setHistoricMoveEvaluations((prev) => prev.slice(0, -1));
    setHistoricEvaluations((prev) => prev.slice(0, -1));
    setHistoricTargetSquares((prev) => prev.slice(0, -1));
    setHistoricNarration((prev) => prev.slice(0, -1));

    let history = game.history();
    if (history.length === 0) {
      handleResetBoard(); // If no moves left, reset the board
      return;
    }

    let formattedHistory = history
      .map((move, index) => {
        const moveNumber = Math.floor(index / 2) + 1;
        return index % 2 === 0
          ? `${moveNumber}. ${move}`
          : `${moveNumber}. ${history[index - 1]} ${move}`;
      })
      .join(" ");
    setPgn(formattedHistory.trim());
  }, [game, handleResetBoard, historicNarration, processNarration]);

  useEffect(() => {
    const worker = new Worker(
      `${process.env.PUBLIC_URL}/js/stockfish-16.1-lite-single.js`
    );
    stockfishWorker.current = worker;

    const engine = new MinervaEngine(worker, { depth: 10 });
    setMinervaEngine(engine);

    return () => {
      worker.terminate();
    };
  }, []);

  useEffect(() => {
    // Clear the rendered image immediately
    setRenderedImage(null);

    const square = historicTargetSquares[historicTargetSquares.length - 1];
    if (!square) return;

    const position = getSquarePosition(square);
    if (!position) return;

    const imageSize = position.size * 0.5; // 50% of the square size

    // Check if there are any move evaluations
    if (!historicMoveEvaluations || historicMoveEvaluations.length === 0) {
      setRenderedImage(null); // No evaluations, clear the image
      return;
    }

    const lastMoveEvaluation =
      historicMoveEvaluations[historicMoveEvaluations.length - 1];

    if (!lastMoveEvaluation) {
      setRenderedImage(null); // No valid evaluation, clear the image
      return;
    }

    // Set a 500ms delay before rendering the image
    const delayTimeout = setTimeout(() => {
      // Check if lastMoveEvaluation is valid before creating the img element
      if (lastMoveEvaluation !== "Error") {
        // Create the img element and store it in state after the delay
        const imageElement = (
          <img
            key={`${square}-${lastMoveEvaluation}`} // Unique key for each image
            src={`img/moveEvaluation/${lastMoveEvaluation.toLowerCase()}.png`}
            alt={lastMoveEvaluation}
            style={{
              position: "absolute",
              top: position.top - position.size * 0.2,
              left:
                position.left +
                (position.size - imageSize) +
                position.size * 0.2,
              width: imageSize, // 50% of the square size
              height: imageSize, // 50% of the square size
              pointerEvents: "none", // Prevent blocking interactions with the chessboard
              opacity: 0, // Initial opacity set to 0 for fade-in effect
              animation: "fadeIn 0.1s forwards",
            }}
          />
        );

        // Set the image in state after the delay
        setRenderedImage(imageElement);
      } else {
        // If lastMoveEvaluation is invalid, clear the image
        setRenderedImage(null);
      }
    }, 750);

    // Cleanup timeout if dependencies change or component unmounts
    return () => clearTimeout(delayTimeout);
  }, [historicTargetSquares, historicMoveEvaluations]);

  useEffect(() => {
    // Add event listeners for left and right arrow keys
    const handleKeyPress = (e) => {
      if (e.key === "ArrowLeft") {
        handleUndo();
      }
    };

    // Attach the event listener to the window
    window.addEventListener("keydown", handleKeyPress);

    // Cleanup by removing the event listener when the component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleUndo]);

  return (
    <Box>
      <Box>
        <Button
          variant="contained"
          onClick={handleResetBoard}
          style={{ marginRight: 10 }}
          startIcon={<RestartAltRoundedIcon />}
          sx={{
            ...(mode === "dark"
              ? {
                  boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)", // Set a glowing shadow for dark mode
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(255, 255, 255, 0.5)", // Increase glow on hover
                  },
                }
              : {
                  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)", // Set a glowing shadow for dark mode
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)", // Increase glow on hover
                  },
                }),
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleUndo}
          style={{ marginRight: 10 }}
          startIcon={<UndoIcon />}
          disabled={game.history().length === 0}
          sx={{
            ...(mode === "dark"
              ? {
                  boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)", // Set a glowing shadow for dark mode
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(255, 255, 255, 0.5)", // Increase glow on hover
                  },
                }
              : {
                  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)", // Set a glowing shadow for dark mode
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)", // Increase glow on hover
                  },
                }),
          }}
        >
          Undo
        </Button>
        <Button
          variant="contained"
          onClick={flipOrientation}
          style={{ marginRight: 10 }}
          startIcon={<SwapVertIcon />}
          sx={{
            ...(mode === "dark"
              ? {
                  boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)", // Set a glowing shadow for dark mode
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(255, 255, 255, 0.5)", // Increase glow on hover
                  },
                }
              : {
                  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)", // Set a glowing shadow for dark mode
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)", // Increase glow on hover
                  },
                }),
          }}
        >
          Flip
        </Button>
        <Box
          sx={{
            p: "20px 0px 0px 0px",
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
            },
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <Box
            id="chessboard"
            ref={chessboardRef}
            sx={{
              width: "100%",
              maxWidth: "500px",
              padding: "0px",
            }}
          >
            <Chessboard
              position={game.fen()}
              onPieceDrop={onDrop}
              boardOrientation={orientation}
              customSquareStyles={getSquareStyles(historicNarration)}
              customPieces={pieceSets["Minerva"]}
              customLightSquareStyle={{
                backgroundColor: themeColors["White Stripe Theme"].lightSquare,
                backgroundImage:
                  themeColors["White Stripe Theme"].lightSquarePattern,
                ...(themeColors["White Stripe Theme"]
                  .lightSquarePatternBlend && {
                  backgroundBlendMode:
                    themeColors["White Stripe Theme"].lightSquarePatternBlend,
                }),
              }}
              customDarkSquareStyle={{
                backgroundColor: themeColors["White Stripe Theme"].darkSquare,
                backgroundImage:
                  themeColors["White Stripe Theme"].darkSquarePattern,
                ...(themeColors["White Stripe Theme"]
                  .darkSquarePatternBlend && {
                  backgroundBlendMode:
                    themeColors["White Stripe Theme"].darkSquarePatternBlend,
                }),
              }}
              areArrowsAllowed={false}
            />
            {renderedImage}
          </Box>

          <Box
            id="description"
            sx={{
              flex: 1,
              width: "100%",
              overflow: "auto",
              pl: {
                xs: 0,
                md: 2,
              },
              mt: {
                xs: 2,
                md: 0,
              },
            }}
          >
            {pgn && (
              <Typography sx={{ mt: 1 }}>
                Moves: <b>{pgn}</b>
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={showMoves}
                  onChange={toggleShowMoves}
                  color="primary"
                  sx={{
                    // Custom styles for checkbox when checked
                    "&.Muichecked": "#4CAF50",
                    // Custom styles for checkbox when unchecked
                    "&.MuiCheckbox-root": {
                      color: mode === "dark" ? "#bdbdbd" : "#bdbdbd", // Checkbox color when unchecked
                    },
                  }}
                />
              }
              label="Show Engine"
              labelPlacement="start"
              sx={{ ml: 0 }}
            />

            {showMoves && (
              <>
                <Typography sx={{ mt: 1 }}>
                  {historicMoveEvaluations.length > 0 && (
                    <>
                      Category:{" "}
                      <b>
                        {
                          historicMoveEvaluations[
                            historicMoveEvaluations.length - 1
                          ]
                        }
                      </b>
                    </>
                  )}
                </Typography>
                {historicEvaluations.length > 0 &&
                  Array.isArray(
                    historicEvaluations[historicEvaluations.length - 1]
                  ) && (
                    <>
                      <Typography sx={{ mt: 1 }}>Top Engine Lines:</Typography>
                      {historicEvaluations[historicEvaluations.length - 1].map(
                        (evalResult, index) => (
                          <Box key={index}>
                            <Typography>
                              {index + 1}: <b>{evalResult.evaluation}</b> :{" "}
                              {evalResult.moves.join(", ")}
                            </Typography>
                          </Box>
                        )
                      )}
                    </>
                  )}
              </>
            )}

            <Box
              id="description"
              sx={{
                flex: 1,
                overflow: "auto",
                maxWidth: "600px",
                pl: { xs: 0, md: 0 },
                mt: { xs: 1, md: 0 },
              }}
            >
              {historicNarration.length > 0 &&
                Array.isArray(
                  historicNarration[historicNarration.length - 1]
                ) && (
                  <>
                    <Typography sx={{ mt: 1, mb: 2 }}>
                      <b>Narration</b>
                    </Typography>
                    {processedNarration.map((narration, index) => {
                      // Access type and description directly from the narration object
                      const { type, sub_type, description } = narration;

                      return (
                        <Paper
                          key={index}
                          onClick={() => setSelectedNarrationType(type)}
                          sx={{
                            ml: 0.2,
                            mb: 0.5,
                            mr: 0.2,
                            p: 1,
                            color: "#000000",
                            backgroundColor:
                              type !== selectedNarrationType
                                ? "#CCCCCC"
                                : "#FFFFFF",
                            wordWrap: "break-word", // Ensures text wraps within the Paper
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              display: "inline",
                              lineHeight: 1.5,
                              whiteSpace: "normal",
                              wordWrap: "break-word", // Ensures long words also wrap
                            }}
                          >
                            {renderNarrationWithColorBoxes(
                              description,
                              type,
                              sub_type
                            )}{" "}
                            {/*<small
                              style={{
                                color: "grey",
                                fontSize: "0.6em",
                                verticalAlign: "super",
                              }}
                            >
                              {priority}
                            </small>*/}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </>
                )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AnalysisBoard;
